import { useState, useRef, useCallback, useEffect } from 'react';
import { ArrowPathIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  useTeamEvent,
  useUpdateTeamEvent,
  useDeleteTeamEvent,
  usePublishTeamEvent,
  useParticipate,
  useUnparticipate,
  useDecline,
  useAddParticipant,
  useMarkNoShow,
  useEventTypesDropdown,
  useUploadEventPhoto,
  useDeleteEventPhoto,
  useUpdateEventScoped,
  useCancelInstance,
  useCancelSeries,
  useEndSeries,
  useSetGuestCount,
} from '../../hooks/useTeamEvents';
import { getEventPhotos, MAX_EVENT_GUESTS, type TeamEventPhoto, type UpdateTeamEventInput, type TeamEventScope } from '../../services/api/teamEvents';
import { useQuery } from '@tanstack/react-query';
import { useAdminUsers } from '../../hooks/useAdmin';
import { useAuth } from '../../services/authContext';
import { Button } from '../../components/common/Button';
import { Modal, ConfirmModal } from '../../components/common/Modal';
import { Spinner } from '../../components/common/Spinner';
import { TimeSelect } from '../../components/common/TimeSelect';
import { SpinWheelModal } from '../../components/team-events/SpinWheelModal';
import { formatName, formatLocalDate, formatDisplayDate } from '../../utils/format';
import { downloadEventIcs } from '../../utils/icsExport';
import { DateField } from '../../components/ui/DateField';
import { GuestCountStepper } from '../../components/team-events/GuestCountStepper';
import { useFlag } from '../../services/flagsContext';
import { clampMaxGuestsPerPerson, countOverLimitParticipants } from '../../utils/teamEventGuests';

const URL_REGEX = /https?:\/\/[^\s<]+/g;

function linkifyText(text: string): React.ReactNode {
  URL_REGEX.lastIndex = 0;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = URL_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const url = match[0];
    parts.push(
      <a key={match.index} href={url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
        {url}
      </a>
    );
    lastIndex = match.index + url.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

export default function TeamEventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const parsedId = id ? parseInt(id, 10) : NaN;
  const eventId = Number.isNaN(parsedId) ? null : parsedId;
  const { user, isAdmin, isHR } = useAuth();
  const externalGuestsOn = useFlag('mks_533_team_event_external_guests');

  const { data: event, isLoading } = useTeamEvent(eventId);
  const { data: eventTypes } = useEventTypesDropdown();
  const { data: allUsers } = useAdminUsers();
  const updateMutation = useUpdateTeamEvent();
  const updateScopedMutation = useUpdateEventScoped();
  const cancelInstanceMutation = useCancelInstance();
  const cancelSeriesMutation = useCancelSeries();
  const endSeriesMutation = useEndSeries();
  const deleteMutation = useDeleteTeamEvent();
  const publishMutation = usePublishTeamEvent();
  const participateMutation = useParticipate();
  const unparticipateMutation = useUnparticipate();
  const declineMutation = useDecline();
  const addParticipantMutation = useAddParticipant();
  const markNoShowMutation = useMarkNoShow();
  const setGuestCountMutation = useSetGuestCount();
  const uploadPhotoMutation = useUploadEventPhoto();
  const deletePhotoMutation = useDeleteEventPhoto();
  const { data: photos } = useQuery({
    queryKey: ['eventPhotos', eventId],
    queryFn: () => getEventPhotos(eventId!),
    enabled: eventId !== null && event?.status === 'published',
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  // Scope-prompt modal state
  const [showScopeModal, setShowScopeModal] = useState(false);
  const [selectedScope, setSelectedScope] = useState<TeamEventScope>('this');
  const [pendingUpdateInput, setPendingUpdateInput] = useState<UpdateTeamEventInput | null>(null);
  const [scopeModalError, setScopeModalError] = useState<string | null>(null);

  // Cancel occurrence modal state
  const [showCancelInstanceModal, setShowCancelInstanceModal] = useState(false);
  const [cancelInstanceError, setCancelInstanceError] = useState<string | null>(null);

  // Series management modal state
  const [showEndSeriesModal, setShowEndSeriesModal] = useState(false);
  const [endSeriesDate, setEndSeriesDate] = useState('');
  const [endSeriesDateError, setEndSeriesDateError] = useState<string | null>(null);
  const [endSeriesError, setEndSeriesError] = useState<string | null>(null);
  const [showCancelSeriesModal, setShowCancelSeriesModal] = useState(false);
  const [cancelSeriesError, setCancelSeriesError] = useState<string | null>(null);

  // Edit form state
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [editEventTypeId, setEditEventTypeId] = useState<number>(0);
  const [editLocation, setEditLocation] = useState('');
  const [editVideoCallUrl, setEditVideoCallUrl] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [editOrganizerIds, setEditOrganizerIds] = useState<number[]>([]);
  const [editRsvpDeadlineDays, setEditRsvpDeadlineDays] = useState<string>('');
  const [editIsInviteOnly, setEditIsInviteOnly] = useState(false);
  const [editAllowExternalGuests, setEditAllowExternalGuests] = useState(false);
  // MKS-540 — the organizer's per-person guest limit while editing. One is the
  // default a freshly flipped switch lands on.
  const [editMaxGuestsPerPerson, setEditMaxGuestsPerPerson] = useState(1);
  // MKS-533 — the stepper reads this so a click lands immediately; the server
  // value flows back in through the effect below once the mutation settles.
  const [guestDraft, setGuestDraft] = useState(0);
  const [watchingMeetClipboard, setWatchingMeetClipboard] = useState(false);
  const meetClipboardIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!watchingMeetClipboard) return;
    // F21-005: clearInterval cancels future ticks but cannot cancel a tick
    // already awaiting clipboard.readText() — if that promise resolves after
    // the user toggled the watcher off (or the page unmounted), the setters
    // would overwrite the manually-typed URL and warn about state-on-unmounted
    // in dev. Capture a cancelled flag in the effect closure and bail before
    // calling setters.
    let cancelled = false;
    meetClipboardIntervalRef.current = setInterval(async () => {
      try {
        const text = await navigator.clipboard.readText();
        if (cancelled) return;
        if (/https:\/\/meet\.google\.com\/[a-z]{3}-[a-z]{4}-[a-z]{3}/.test(text)) {
          setEditVideoCallUrl(text);
          setWatchingMeetClipboard(false);
        }
      } catch { /* clipboard permission denied */ }
    }, 1000);
    return () => {
      cancelled = true;
      if (meetClipboardIntervalRef.current) {
        clearInterval(meetClipboardIntervalRef.current);
        meetClipboardIntervalRef.current = null;
      }
    };
  }, [watchingMeetClipboard]);

  useEffect(() => {
    setGuestDraft(event?.myGuestCount ?? 0);
  }, [event?.myGuestCount]);

  // Spin wheel state
  const [showSpinWheel, setShowSpinWheel] = useState(false);

  // Add participant state
  const [showAddUser, setShowAddUser] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [addStatus, setAddStatus] = useState<'going' | 'not_going'>('going');

  const activeUsers = allUsers?.filter((u) => !u.deletedAt) ?? [];
  const isOrganizer = event ? event.organizers.some((o) => o.id === user?.id) : false;
  const canEdit = event ? (event.createdBy.id === user?.id || isOrganizer || isAdmin || isHR) : false;

  const startEditing = () => {
    if (!event) return;
    setEditTitle(event.title || '');
    setEditDescription(event.description || '');
    setEditDate(event.eventDate);
    setEditTime(event.eventTime || '');
    setEditLocation(event.location || '');
    setEditVideoCallUrl(event.videoCallUrl || '');
    setEditEndDate(event.endDate || event.eventDate);
    setEditEndTime(event.endTime || '');
    setEditEventTypeId(event.eventType.id);
    setEditOrganizerIds(event.organizers.map((o) => o.id));
    setEditRsvpDeadlineDays(event.rsvpDeadlineDays?.toString() ?? '');
    setEditIsInviteOnly(event.isInviteOnly);
    setEditAllowExternalGuests(event.allowExternalGuests);
    // An event open to guests carries its effective limit (20 on one that
    // predates MKS-540); one that is not opens the field at 1.
    setEditMaxGuestsPerPerson(event.maxGuestsPerPerson ?? 1);
    setEditError(null);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditError(null);
  };

  const buildEditInput = (): UpdateTeamEventInput => ({
    eventTypeId: editEventTypeId,
    title: editTitle || null,
    description: editDescription || null,
    location: editLocation || null,
    videoCallUrl: editVideoCallUrl || null,
    eventDate: editDate,
    eventTime: editTime || null,
    endDate: editEndDate || null,
    endTime: editEndTime || null,
    organizerIds: editOrganizerIds,
    rsvpDeadlineDays: editRsvpDeadlineDays ? parseInt(editRsvpDeadlineDays, 10) : null,
    isInviteOnly: editIsInviteOnly,
    allowExternalGuests: editAllowExternalGuests,
    // Switching guests off leaves the stored limit alone — the DTO hides it.
    maxGuestsPerPerson: editAllowExternalGuests ? editMaxGuestsPerPerson : undefined,
  });

  const handleSave = async () => {
    if (!event) return;
    setEditError(null);
    // Recurring events get a scope-prompt modal before the API call
    if (event.seriesId !== null) {
      setPendingUpdateInput(buildEditInput());
      setSelectedScope('this');
      setScopeModalError(null);
      setShowScopeModal(true);
      return;
    }
    // Non-recurring: save directly
    try {
      await updateMutation.mutateAsync({ id: event.id, input: buildEditInput() });
      setIsEditing(false);
    } catch (err) {
      setEditError((err as Error).message);
    }
  };

  const handleConfirmScopedSave = async () => {
    if (!event || !pendingUpdateInput) return;
    setScopeModalError(null);
    try {
      await updateScopedMutation.mutateAsync({
        id: event.id,
        input: pendingUpdateInput,
        scope: selectedScope,
      });
      setIsEditing(false);
      setShowScopeModal(false);
      setPendingUpdateInput(null);
    } catch (err) {
      setScopeModalError((err as Error).message);
    }
  };

  const handleCancelInstance = async () => {
    if (!event) return;
    setCancelInstanceError(null);
    try {
      await cancelInstanceMutation.mutateAsync(event.id);
      setShowCancelInstanceModal(false);
    } catch (err) {
      setCancelInstanceError((err as Error).message);
    }
  };

  const handleEndSeries = async () => {
    if (!event) return;
    const today = new Date().toISOString().slice(0, 10);
    if (endSeriesDate < today) {
      setEndSeriesDateError('End date must be today or in the future.');
      return;
    }
    setEndSeriesDateError(null);
    setEndSeriesError(null);
    try {
      await endSeriesMutation.mutateAsync({ seriesId: event.seriesId!, endDate: endSeriesDate });
      setShowEndSeriesModal(false);
    } catch (err) {
      setEndSeriesError((err as Error).message);
    }
  };

  const handleCancelSeries = async () => {
    if (!event) return;
    setCancelSeriesError(null);
    try {
      await cancelSeriesMutation.mutateAsync(event.seriesId!);
      navigate('/hr/team-events');
    } catch (err) {
      setCancelSeriesError((err as Error).message);
    }
  };

  const handleDelete = async () => {
    if (!event) return;
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync(event.id);
      navigate('/hr/team-events');
    } catch (err) {
      setDeleteError((err as Error).message);
    }
  };

  const isMutating = participateMutation.isPending || unparticipateMutation.isPending || declineMutation.isPending;

  const addToCalendar = useCallback(async () => {
    if (!event) return;
    await downloadEventIcs({
      id: event.id,
      title: event.title,
      eventTypeName: event.eventType.name,
      eventDate: event.eventDate,
      eventTime: event.eventTime,
      endDate: event.endDate,
      endTime: event.endTime,
      location: event.location,
      videoCallUrl: event.videoCallUrl,
      description: event.description,
    });
  }, [event]);

  const handleAddParticipant = async () => {
    if (!selectedUserId || !event) return;
    try {
      await addParticipantMutation.mutateAsync({ eventId: event.id, userId: Number(selectedUserId), status: addStatus });
      setSelectedUserId('');
      setAddStatus('going');
      setShowAddUser(false);
    } catch {
      // Error is handled by mutation state
    }
  };

  const handleAddOrganizer = (userId: number) => {
    if (!editOrganizerIds.includes(userId)) {
      setEditOrganizerIds([...editOrganizerIds, userId]);
    }
  };

  const handleRemoveOrganizer = (userId: number) => {
    setEditOrganizerIds(editOrganizerIds.filter((id) => id !== userId));
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !event) return;
    await uploadPhotoMutation.mutateAsync({ eventId: event.id, file });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const canDeletePhoto = (photo: TeamEventPhoto) =>
    photo.uploadedBy.id === user?.id || event?.createdBy.id === user?.id || isAdmin || isHR;

  if (isLoading) {
    return <div className="flex justify-center py-8"><Spinner /></div>;
  }

  if (!event) {
    return (
      <div className="space-y-4">
        <Link to="/hr/team-events" className="text-primary-600 hover:text-primary-800 text-sm font-medium">
          &larr; Back to Team Events
        </Link>
        <p className="text-gray-500">Event not found.</p>
      </div>
    );
  }

  const isPast = event.eventDate < formatLocalDate(new Date());
  const rsvpClosed = (() => {
    if (event.rsvpDeadlineDays === null) return false;
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const deadline = new Date(event.eventDate + 'T00:00:00Z');
    deadline.setUTCDate(deadline.getUTCDate() - event.rsvpDeadlineDays);
    return today >= deadline;
  })();
  // MKS-533 — headcount is monks plus the guests they bring; `event.guestCount`
  // is already 0 for an event that is not open to guests, so an event without
  // the switch reads exactly as it did before.
  const guestsEnabled = externalGuestsOn && event.allowExternalGuests;
  // MKS-540 — the organizer's per-person limit. The DTO already resolves the
  // legacy fallback, so a null here only ever means "guests are off".
  const perPersonLimit = guestsEnabled ? event.maxGuestsPerPerson ?? MAX_EVENT_GUESTS : null;
  // Lowering a limit trims nobody, so the organizer is told who sits above it.
  const overLimitCount =
    perPersonLimit === null ? 0 : countOverLimitParticipants(event.participants, perPersonLimit);
  const participantIdSet = new Set(event.participants.map((p) => p.id));
  const organizerOnlyCount = event.organizers.filter((o) => !participantIdSet.has(o.id)).length;
  const attendingCount = event.participants.filter((p) => !p.noShow).length + organizerOnlyCount;
  const guestTotal = guestsEnabled ? event.guestCount : 0;
  const totalHeadcount = attendingCount + guestTotal;
  const maxParticipants = event.eventType.maxParticipants;
  // Guests the signed-in user may still claim: the cap minus everyone else's heads minus their own seat.
  const guestSeatsLeft =
    maxParticipants === null
      ? MAX_EVENT_GUESTS
      : Math.max(maxParticipants - (totalHeadcount - event.myGuestCount), 0);
  const isFull = maxParticipants !== null &&
    totalHeadcount >= maxParticipants &&
    !event.isParticipating;

  const handleGuestChange = (next: number) => {
    if (next === guestDraft) return;
    setGuestDraft(next);
    setGuestCountMutation.mutate(
      { eventId: event.id, guestCount: next },
      { onError: () => setGuestDraft(event.myGuestCount) },
    );
  };
  const excludedUserIds = new Set([
    ...event.participants.map((p) => p.id),
    ...event.declinedUsers.map((u) => u.id),
    ...event.organizers.map((o) => o.id),
  ]);
  const availableUsers = activeUsers.filter((u) => !excludedUserIds.has(u.id));

  return (
    <div className="space-y-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Link to="/hr/team-events" className="text-primary-600 hover:text-primary-800 text-sm font-medium">
          &larr; Back to Team Events
        </Link>
      </div>

      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          {isEditing ? (
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder="Event title (optional)"
              className="text-2xl font-semibold text-gray-900 border border-gray-300 rounded-lg px-3 py-1"
            />
          ) : (
            <h1 className="text-2xl font-semibold text-gray-900">
              {event.title || event.eventType.name}
            </h1>
          )}
          {!isEditing && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
              {event.eventType.name}
            </span>
          )}
          {!isEditing && event.status === 'draft' && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-800 border border-yellow-200">
              Draft
            </span>
          )}
          {!isEditing && event.isDetached && isAdmin && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
              Edited separately
            </span>
          )}
        </div>
        {canEdit && event.status !== 'cancelled' && (
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button size="sm" onClick={handleSave} isLoading={updateMutation.isPending || updateScopedMutation.isPending}>Save</Button>
                <Button size="sm" variant="secondary" onClick={cancelEditing}>Cancel</Button>
              </>
            ) : (
              <>
                {event.status === 'draft' && (
                  <Button size="sm" onClick={() => publishMutation.mutate(event.id)} isLoading={publishMutation.isPending}>Publish</Button>
                )}
                <Button size="sm" variant="secondary" onClick={startEditing}>Edit</Button>
                {event.seriesId !== null && (
                  <Button size="sm" variant="danger" onClick={() => setShowCancelInstanceModal(true)}>
                    Cancel occurrence
                  </Button>
                )}
                <Button size="sm" variant="danger" onClick={() => setShowDelete(true)}>Delete</Button>
              </>
            )}
          </div>
        )}
      </div>

      {editError && <p className="text-sm text-red-600">{editError}</p>}

      {/* Cancelled occurrence banner */}
      {event.status === 'cancelled' && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">
          <p className="text-sm font-semibold text-red-700">This occurrence was cancelled.</p>
        </div>
      )}

      {/* Info card + Map side by side */}
      <div className={`flex gap-6 ${!isEditing && event.location ? 'items-stretch' : ''}`}>
        {/* Info card */}
        <div className={`bg-white rounded-lg border border-gray-200 p-6 space-y-4 ${!isEditing && event.location ? 'flex-1 min-w-0' : 'w-full'}`}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase">Date</label>
              {isEditing ? (
                <DateField
                  value={editDate}
                  onChange={(nextDate) => { setEditDate(nextDate); if (!editEndDate) setEditEndDate(nextDate); }}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              ) : (
                <p className="mt-1 text-sm text-gray-900">{formatDisplayDate(event.eventDate)}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase">Time</label>
              {isEditing ? (
                <TimeSelect
                  value={editTime}
                  onChange={(v) => { setEditTime(v); if (v && !editEndTime) { const parts = v.split(':').map(Number); const h = parts[0] ?? 0; const m = parts[1] ?? 0; setEditEndTime(`${String((h + 1) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`); } }}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              ) : (
                <p className="mt-1 text-sm text-gray-900">{event.eventTime || '—'}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase">End Date</label>
              {isEditing ? (
                <DateField
                  value={editEndDate}
                  onChange={setEditEndDate}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              ) : (
                <p className="mt-1 text-sm text-gray-900">{event.endDate ? formatDisplayDate(event.endDate) : '—'}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase">End Time</label>
              {isEditing ? (
                <TimeSelect
                  value={editEndTime}
                  onChange={setEditEndTime}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              ) : (
                <p className="mt-1 text-sm text-gray-900">{event.endTime || '—'}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase">Event Type</label>
            {isEditing ? (
              <select
                value={editEventTypeId}
                onChange={(e) => setEditEventTypeId(Number(e.target.value))}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                {eventTypes?.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            ) : (
              <p className="mt-1 text-sm text-gray-900">{event.eventType.name}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase">RSVP Deadline</label>
            {isEditing ? (
              <input
                type="number"
                min="1"
                value={editRsvpDeadlineDays}
                onChange={(e) => setEditRsvpDeadlineDays(e.target.value)}
                placeholder="No deadline"
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            ) : (
              <p className="mt-1 text-sm text-gray-900">
                {event.rsvpDeadlineDays ? `${event.rsvpDeadlineDays} days before event` : '—'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase">Visibility</label>
            {isEditing ? (
              <label className="mt-1 flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editIsInviteOnly}
                  onChange={(e) => setEditIsInviteOnly(e.target.checked)}
                  className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700">Invite only</span>
              </label>
            ) : (
              <p className="mt-1 text-sm text-gray-900">
                {event.isInviteOnly ? 'Invite only' : 'Everyone'}
              </p>
            )}
          </div>

          {externalGuestsOn && (
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase">External Guests</label>
              {isEditing ? (
                <div className="mt-1 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editAllowExternalGuests}
                      onChange={(e) => {
                        setEditAllowExternalGuests(e.target.checked);
                        // MKS-540 — the switch always comes back on at +1.
                        if (e.target.checked) setEditMaxGuestsPerPerson(1);
                      }}
                      className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-700">Participants can bring guests</span>
                  </label>
                  {editAllowExternalGuests && (
                    <div>
                      <label
                        className="block text-xs font-medium text-gray-500 uppercase"
                        htmlFor="edit-max-guests-per-person"
                      >
                        Max guests per person
                      </label>
                      <input
                        id="edit-max-guests-per-person"
                        type="number"
                        min={1}
                        max={MAX_EVENT_GUESTS}
                        value={editMaxGuestsPerPerson}
                        onChange={(e) =>
                          setEditMaxGuestsPerPerson(clampMaxGuestsPerPerson(Number(e.target.value)))
                        }
                        className="mt-1 block w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <p className="mt-1 text-sm text-gray-900">
                    {event.allowExternalGuests ? 'Participants can bring guests' : 'Monks only'}
                  </p>
                  {/* MKS-540 — the limit itself, so nobody has to open the editor to read it. */}
                  {perPersonLimit !== null && (
                    <p className="mt-1 text-sm text-gray-500">
                      Guests: up to +{perPersonLimit} per person
                    </p>
                  )}
                  {canEdit && overLimitCount > 0 && (
                    <p className="mt-1 text-sm text-amber-700">
                      {overLimitCount} {overLimitCount === 1 ? 'person has' : 'people have'} more
                      guests than the new limit
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase">Created By</label>
            <p className="mt-1 text-sm text-gray-900">{formatName(event.createdBy)}</p>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="block text-xs font-medium text-gray-500 tracking-wider">MCs</label>
              {isEditing && (
                <button
                  type="button"
                  onClick={() => setShowSpinWheel(true)}
                  className="text-xs font-medium text-primary-600 hover:text-primary-800"
                >
                  Spin Wheel
                </button>
              )}
            </div>
            {isEditing ? (
              <div className="mt-1 space-y-2">
                {editOrganizerIds.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {editOrganizerIds.map((orgId) => {
                      const u = activeUsers.find((u) => u.id === orgId);
                      return (
                        <span
                          key={orgId}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                        >
                          {u ? formatName(u) : `User #${orgId}`}
                          <button
                            type="button"
                            onClick={() => handleRemoveOrganizer(orgId)}
                            className="text-blue-400 hover:text-blue-600"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) handleAddOrganizer(Number(e.target.value));
                  }}
                  className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Add MC...</option>
                  {activeUsers
                    .filter((u) => !editOrganizerIds.includes(u.id))
                    .map((u) => (
                      <option key={u.id} value={u.id}>{formatName(u)}</option>
                    ))}
                </select>
              </div>
            ) : (
              <div className="mt-1 flex flex-wrap gap-1">
                {event.organizers.length > 0 ? (
                  event.organizers.map((o) => (
                    <span
                      key={o.id}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                    >
                      {formatName(o)}
                    </span>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">—</p>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase">Location</label>
            {isEditing ? (
              <input
                type="text"
                value={editLocation}
                onChange={(e) => setEditLocation(e.target.value)}
                placeholder="e.g. Prague, Karlovo namesti 1"
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            ) : (
              <p className="mt-1 text-sm text-gray-900">{event.location || '—'}</p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="block text-xs font-medium text-gray-500 uppercase">Video Call</label>
              {isEditing && (
                <button
                  type="button"
                  onClick={() => {
                    window.open('https://meet.google.com/landing', '_blank');
                    setWatchingMeetClipboard(true);
                  }}
                  className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                >
                  {watchingMeetClipboard ? '⏳ Waiting for clipboard…' : 'Create Google Meet link'}
                </button>
              )}
            </div>
            {isEditing ? (
              <input
                type="url"
                value={editVideoCallUrl}
                onChange={(e) => setEditVideoCallUrl(e.target.value)}
                placeholder="e.g. https://meet.google.com/abc-defg-hij"
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            ) : (
              <p className="mt-1 text-sm text-gray-900">
                {(() => {
                  const safeVideoCallUrl = event.videoCallUrl && /^https?:\/\//i.test(event.videoCallUrl) ? event.videoCallUrl : null;
                  return safeVideoCallUrl ? (
                    <a href={safeVideoCallUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline break-all">
                      {safeVideoCallUrl}
                    </a>
                  ) : '—';
                })()}
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase">Description</label>
            {isEditing ? (
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Optional description"
                rows={6}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            ) : (
              <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                {event.description ? linkifyText(event.description) : '—'}
              </p>
            )}
          </div>
        </div>

        {/* Google Maps embed */}
        {!isEditing && event.location && (
          <div className="w-[28rem] flex-shrink-0 flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden">
            <iframe
              title="Event location"
              width="100%"
              className="flex-1"
              style={{ border: 0, minHeight: 300 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://www.google.com/maps?q=${encodeURIComponent(event.location)}&output=embed`}
            />
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center text-sm font-medium text-primary-600 hover:text-primary-800 py-2 border-t border-gray-200"
            >
              Open in Maps &#x2197;
            </a>
          </div>
        )}
      </div>

      {/* Part of a recurring series */}
      {event.seriesId && event.seriesSiblings && event.seriesSiblings.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <ArrowPathIcon className="h-4 w-4 text-gray-400" />
            Part of a recurring series
          </h3>
          <div className="flex flex-wrap gap-2">
            {event.seriesSiblings.map((sibling) => (
              <Link
                key={sibling.id}
                to={`/hr/team-events/${sibling.id}`}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  sibling.status === 'cancelled'
                    ? 'bg-red-50 text-red-400 line-through'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {/* MKS-351 — dd.mm.yyyy; the shared util reads the YYYY-MM-DD as a
                    calendar date, so no timeZone pinning is needed. */}
                {formatDisplayDate(sibling.eventDate)}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Series management card — admin only, non-cancelled instances in a series */}
      {isAdmin && event.seriesId !== null && event.status !== 'cancelled' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Series management</h3>
          <div className="flex gap-3">
            <Button size="sm" variant="secondary" onClick={() => { setEndSeriesDate(''); setEndSeriesDateError(null); setEndSeriesError(null); setShowEndSeriesModal(true); }}>
              End series on date…
            </Button>
            <Button size="sm" variant="danger" onClick={() => { setCancelSeriesError(null); setShowCancelSeriesModal(true); }}>
              Cancel series
            </Button>
          </div>
        </div>
      )}

      {/* Participation */}
      {!isEditing && !isPast && event.status === 'published' && (
        rsvpClosed ? (
          <div className="flex gap-2 items-center">
            <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-400">
              {event.isParticipating ? '✓ Going' : event.isDeclined ? '✗ Not Going' : 'RSVP Closed'}
            </span>
            {!event.isParticipating && !event.isDeclined && (
              <span className="text-sm text-gray-500">RSVPs closed {event.rsvpDeadlineDays} days before event</span>
            )}
          </div>
        ) : (
          <div className="flex gap-2 items-center flex-wrap">
            {event.isParticipating ? (
              <>
                <button
                  onClick={() => unparticipateMutation.mutate(event.id)}
                  disabled={isMutating}
                  className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors bg-green-100 text-green-800 hover:bg-green-200"
                >
                  ✓ Going
                </button>
                {guestsEnabled && (
                  <GuestCountStepper
                    value={guestDraft}
                    onChange={handleGuestChange}
                    // MKS-540 — the smaller of the seats left and the per-person limit.
                    max={Math.min(guestSeatsLeft, perPersonLimit ?? MAX_EVENT_GUESTS)}
                    disabled={isMutating || setGuestCountMutation.isPending}
                  />
                )}
              </>
            ) : event.isDeclined ? (
              <button
                onClick={() => participateMutation.mutate(event.id)}
                disabled={isMutating}
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors bg-red-100 text-red-800 hover:bg-red-200"
              >
                ✗ Not Going
              </button>
            ) : (
              <>
                <button
                  onClick={() => participateMutation.mutate(event.id)}
                  disabled={isMutating || isFull}
                  className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isFull
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-800'
                  }`}
                >
                  {isFull ? 'Full' : 'Join'}
                </button>
                {!isFull && (
                  <button
                    onClick={() => declineMutation.mutate(event.id)}
                    disabled={isMutating}
                    className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors bg-gray-100 text-gray-600 hover:bg-red-100 hover:text-red-800"
                  >
                    Not Going
                  </button>
                )}
              </>
            )}
          </div>
        )
      )}

      {/* Add to calendar / Download ICS */}
      {!isEditing && (event.status === 'published' || event.status === 'cancelled') && (
        <div>
          {event.seriesId !== null ? (
            <a
              href={`/api/team-events/${event.id}/ics`}
              download
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
            >
              <CalendarDaysIcon className="h-4 w-4" />
              Download series calendar (.ics)
            </a>
          ) : (
            <button
              onClick={addToCalendar}
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
            >
              <CalendarDaysIcon className="h-4 w-4" />
              Add to my calendar
            </button>
          )}
        </div>
      )}

      {/* Participants */}
      {event.status === 'published' && <div className="bg-white rounded-lg border border-gray-200 p-6">
        {(() => {
          const organizerIds = new Set(event.organizers.map((o) => o.id));
          const participantIds = new Set(event.participants.map((p) => p.id));
          const organizerOnlyUsers = event.organizers.filter((o) => !participantIds.has(o.id));
          const sortedParticipants = [
            ...event.participants.filter((p) => organizerIds.has(p.id)),
            ...event.participants.filter((p) => !organizerIds.has(p.id)),
          ];

          return (
            <>
              <h2 className="text-lg font-medium text-gray-900 mb-1">
                Participants ({maxParticipants
                  ? `${totalHeadcount} / ${maxParticipants}`
                  : totalHeadcount})
              </h2>
              {/* MKS-533 — spell the headcount out once guests are in it. */}
              {guestTotal > 0 ? (
                <p className="text-sm text-gray-500 mb-4">
                  {attendingCount} {attendingCount === 1 ? 'monk' : 'monks'} + {guestTotal}{' '}
                  {guestTotal === 1 ? 'guest' : 'guests'} = {totalHeadcount}
                </p>
              ) : (
                <div className="mb-4" />
              )}

              {sortedParticipants.length === 0 && organizerOnlyUsers.length === 0 ? (
                <p className="text-sm text-gray-500">No participants yet.</p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {organizerOnlyUsers.map((o) => (
                    <div key={o.id} className="py-2 flex items-center justify-between">
                      <span className="text-sm text-gray-900">
                        <span className="text-amber-500 mr-1.5" title="MC">&#9733;</span>
                        {formatName(o)}
                      </span>
                      {isPast && canEdit && (
                        <button
                          type="button"
                          onClick={() => markNoShowMutation.mutate({ eventId: event.id, userId: o.id, noShow: true })}
                          disabled={markNoShowMutation.isPending}
                          className="text-xs font-medium px-2 py-0.5 rounded-full transition-colors bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-600"
                        >
                          Present
                        </button>
                      )}
                    </div>
                  ))}
                  {sortedParticipants.map((p) => (
                    <div key={p.id} className="py-2 flex items-center justify-between">
                      <span className={`text-sm ${p.noShow ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                        {organizerIds.has(p.id) && (
                          <span className="text-amber-500 mr-1.5 no-underline inline-block" style={{ textDecoration: 'none' }} title="MC">&#9733;</span>
                        )}
                        {formatName(p)}
                        {guestsEnabled && p.guestCount > 0 && (
                          <span className="ml-1.5 text-xs font-semibold text-gray-500" title={`Bringing ${p.guestCount} guest${p.guestCount === 1 ? '' : 's'}`}>
                            +{p.guestCount}
                          </span>
                        )}
                        {p.noShow && !canEdit && (
                          <span className="ml-2 text-xs text-gray-400 no-underline inline-block" style={{ textDecoration: 'none' }}>(no-show)</span>
                        )}
                      </span>
                      <div className="flex items-center gap-2">
                        {isPast && canEdit && (
                          <button
                            type="button"
                            onClick={() => markNoShowMutation.mutate({ eventId: event.id, userId: p.id, noShow: !p.noShow })}
                            disabled={markNoShowMutation.isPending}
                            className={`text-xs font-medium px-2 py-0.5 rounded-full transition-colors ${
                              p.noShow
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : 'bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-600'
                            }`}
                          >
                            {p.noShow ? 'No-show' : 'Present'}
                          </button>
                        )}
                        {isPast && !canEdit && p.noShow && (
                          <span className="text-xs text-red-400 px-2 py-0.5">No-show</span>
                        )}
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => addParticipantMutation.mutate({ eventId: event.id, userId: p.id, status: 'not_going' })}
                            disabled={addParticipantMutation.isPending}
                            className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                          >
                            Not Going
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          );
        })()}

        {event.declinedUsers && event.declinedUsers.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-500 mb-2">
              Not Going ({event.declinedUsers.length})
            </h3>
            <div className="divide-y divide-gray-100">
              {event.declinedUsers.map((p) => (
                <div key={p.id} className="py-2 flex items-center justify-between text-sm text-gray-400">
                  <span>{formatName(p)}</span>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => addParticipantMutation.mutate({ eventId: event.id, userId: p.id, status: 'going' })}
                      disabled={addParticipantMutation.isPending}
                      className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 hover:bg-green-50 hover:text-green-700 transition-colors"
                    >
                      Going
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {(!isPast || canEdit) && (
          <div className="mt-4">
            {!showAddUser ? (
              <Button size="sm" variant="secondary" onClick={() => setShowAddUser(true)}>
                Add Participant
              </Button>
            ) : (
              <div className="flex gap-2 items-center">
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value ? Number(e.target.value) : '')}
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Select user...</option>
                  {availableUsers.map((u) => (
                    <option key={u.id} value={u.id}>{formatName(u)}</option>
                  ))}
                </select>
                <select
                  value={addStatus}
                  onChange={(e) => setAddStatus(e.target.value as 'going' | 'not_going')}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="going">Going</option>
                  <option value="not_going">Not Going</option>
                </select>
                <Button
                  size="sm"
                  onClick={handleAddParticipant}
                  disabled={!selectedUserId}
                  isLoading={addParticipantMutation.isPending}
                >
                  Add
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowAddUser(false)}>Cancel</Button>
              </div>
            )}
          </div>
        )}
      </div>}

      {/* Photos */}
      {!isEditing && event.status === 'published' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">
              Photos {photos && photos.length > 0 ? `(${photos.length})` : ''}
            </h2>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <Button
                size="sm"
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                isLoading={uploadPhotoMutation.isPending}
              >
                Upload Photo
              </Button>
            </div>
          </div>
          {uploadPhotoMutation.isError && (
            <p className="text-sm text-red-600 mb-3">{(uploadPhotoMutation.error as Error).message}</p>
          )}
          {photos && photos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {photos.map((photo) => (
                <div key={photo.id} className="group relative rounded-lg overflow-hidden bg-gray-100">
                  <a href={photo.url} target="_blank" rel="noopener noreferrer">
                    <img
                      src={photo.url}
                      alt={photo.fileName}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-3 py-2">
                      <p className="text-white text-sm font-medium drop-shadow-lg">
                        {event.title || event.eventType.name}
                      </p>
                    </div>
                  </a>
                  {canDeletePhoto(photo) && (
                    <button
                      onClick={() => deletePhotoMutation.mutate({ eventId: event.id, photoId: photo.id })}
                      disabled={deletePhotoMutation.isPending}
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 hover:bg-black/70 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm"
                      title="Delete photo"
                    >
                      &times;
                    </button>
                  )}
                  <p className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 text-white text-xs rounded px-1.5 py-0.5">
                    {formatName(photo.uploadedBy)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No photos yet. Be the first to upload!</p>
          )}
        </div>
      )}

      {/* Delete confirmation */}
      <ConfirmModal
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={handleDelete}
        title="Delete Event"
        message="Are you sure you want to delete this event? All participant data will be lost."
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
        error={deleteError}
      />

      {/* Cancel occurrence confirmation */}
      <ConfirmModal
        isOpen={showCancelInstanceModal}
        onClose={() => setShowCancelInstanceModal(false)}
        onConfirm={handleCancelInstance}
        title="Cancel this occurrence"
        message="This will cancel the event and notify all confirmed participants. The other events in the series are not affected."
        confirmText="Cancel occurrence"
        cancelText="Keep event"
        variant="danger"
        isLoading={cancelInstanceMutation.isPending}
        error={cancelInstanceError}
      />

      {/* Scope-prompt modal */}
      <Modal
        isOpen={showScopeModal}
        onClose={() => setShowScopeModal(false)}
        title="Save changes to recurring event"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowScopeModal(false)}>
              Keep editing
            </Button>
            <Button
              variant="primary"
              isLoading={updateScopedMutation.isPending}
              onClick={handleConfirmScopedSave}
            >
              Save changes
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-700 mb-3">How would you like to apply your edits?</p>
        <div role="radiogroup" aria-label="Scope of changes" className="space-y-1">
          <label className={`flex items-start gap-3 py-3 px-3 rounded-lg cursor-pointer hover:bg-gray-50 has-[:checked]:bg-blue-50 has-[:checked]:border has-[:checked]:border-blue-200`}>
            <input
              type="radio"
              name="scope"
              value="this"
              checked={selectedScope === 'this'}
              onChange={() => setSelectedScope('this')}
              className="mt-0.5 shrink-0"
            />
            <div>
              <p className="text-sm font-semibold text-gray-900">This event only</p>
              <p className="text-xs text-gray-500 mt-0.5">Only this occurrence changes.</p>
            </div>
          </label>
          <label className={`flex items-start gap-3 py-3 px-3 rounded-lg cursor-pointer hover:bg-gray-50 has-[:checked]:bg-blue-50 has-[:checked]:border has-[:checked]:border-blue-200`}>
            <input
              type="radio"
              name="scope"
              value="this_and_following"
              checked={selectedScope === 'this_and_following'}
              onChange={() => setSelectedScope('this_and_following')}
              className="mt-0.5 shrink-0"
            />
            <div>
              <p className="text-sm font-semibold text-gray-900">This and following events</p>
              <p className="text-xs text-gray-500 mt-0.5">
                This occurrence and all future ones in the series change (except events already edited separately).
              </p>
            </div>
          </label>
          <label className={`flex items-start gap-3 py-3 px-3 rounded-lg cursor-pointer hover:bg-gray-50 has-[:checked]:bg-blue-50 has-[:checked]:border has-[:checked]:border-blue-200`}>
            <input
              type="radio"
              name="scope"
              value="entire_series"
              checked={selectedScope === 'entire_series'}
              onChange={() => setSelectedScope('entire_series')}
              className="mt-0.5 shrink-0"
            />
            <div>
              <p className="text-sm font-semibold text-gray-900">Entire series</p>
              <p className="text-xs text-gray-500 mt-0.5">
                All future occurrences change (except events already edited separately).
              </p>
            </div>
          </label>
        </div>
        {scopeModalError && <p className="text-sm text-red-600 mt-2">{scopeModalError}</p>}
      </Modal>

      {/* End series modal */}
      <Modal
        isOpen={showEndSeriesModal}
        onClose={() => setShowEndSeriesModal(false)}
        title="End series from date"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowEndSeriesModal(false)}>
              Never mind
            </Button>
            <Button
              variant="primary"
              isLoading={endSeriesMutation.isPending}
              onClick={handleEndSeries}
            >
              End series
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stop generating new events from:
            </label>
            <DateField
              value={endSeriesDate}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(nextDate) => { setEndSeriesDate(nextDate); setEndSeriesDateError(null); }}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            {endSeriesDateError && (
              <p className="text-sm text-red-600 mt-1">{endSeriesDateError}</p>
            )}
          </div>
          <p className="text-xs text-gray-500">
            All occurrences after this date will be permanently removed. No notifications are sent.
          </p>
          {endSeriesError && <p className="text-sm text-red-600">{endSeriesError}</p>}
        </div>
      </Modal>

      {/* Cancel series confirmation */}
      <ConfirmModal
        isOpen={showCancelSeriesModal}
        onClose={() => setShowCancelSeriesModal(false)}
        onConfirm={handleCancelSeries}
        title="Cancel entire series"
        message="This will cancel all upcoming occurrences and notify confirmed participants for each one. Past events are not affected."
        confirmText="Cancel series"
        cancelText="Keep series"
        variant="danger"
        isLoading={cancelSeriesMutation.isPending}
        error={cancelSeriesError}
      />

      {/* Spin Wheel modal for organizer selection */}
      {showSpinWheel && event && (
        <SpinWheelModal
          isOpen
          onClose={() => setShowSpinWheel(false)}
          eventTypeId={isEditing ? editEventTypeId : event.eventType.id}
          excludeUserIds={editOrganizerIds}
          onSelect={(user) => handleAddOrganizer(user.id)}
          eventDate={isEditing ? editDate : event.eventDate}
          eventId={event.id}
        />
      )}
    </div>
  );
}
